import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Layout,
  MessageSquare,
  MousePointerClick,
  AtSign,
  Layers,
  Activity,
  Code2,
  type LucideIcon,
} from 'lucide-react';
import type { ThemeCategoryConfig, ThemeTokens } from '../../types/theme';
import { ColorPickerControl } from './ColorPickerControl';

interface ThemeCategoryAccordionProps {
  config: ThemeCategoryConfig;
  tokens: ThemeTokens;
  defaultTokens: ThemeTokens;
  onChangeToken: (key: keyof ThemeTokens, value: string) => void;
  onResetCategory: (keys: Array<keyof ThemeTokens>) => void;
  defaultOpen?: boolean;
}

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  Layout,
  MessageSquare,
  MousePointerClick,
  AtSign,
  Layers,
  Activity,
  Code2,
};

export const ThemeCategoryAccordion: React.FC<ThemeCategoryAccordionProps> = ({
  config,
  tokens,
  defaultTokens,
  onChangeToken,
  onResetCategory,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const IconComponent = CATEGORY_ICONS[config.icon] || Layout;

  const modifiedCount = config.fields.filter(
    (field) => tokens[field.key]?.toLowerCase() !== defaultTokens[field.key]?.toLowerCase()
  ).length;

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    onResetCategory(config.fields.map((f) => f.key));
  };

  return (
    <div className="rounded-3xl border border-border bg-card/60 overflow-hidden shadow-xs transition-all duration-200">
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4.5 flex items-center justify-between text-left hover:bg-muted/40 transition gap-3"
      >
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 shadow-2xs">
            <IconComponent className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-foreground">{config.title}</span>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border/60">
                {config.fields.length} tokens
              </span>
              {modifiedCount > 0 && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/20">
                  {modifiedCount} edited
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{config.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {modifiedCount > 0 && (
            <button
              type="button"
              onClick={handleReset}
              className="text-[11px] font-semibold px-2.5 py-1 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted border border-border transition flex items-center gap-1"
              title="Reset this category to default"
            >
              <RotateCcw className="h-3 w-3" />
              <span>Reset</span>
            </button>
          )}

          <div className="w-7 h-7 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-muted transition">
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </div>
      </button>

      {/* Accordion Body */}
      {isOpen && (
        <div className="p-4 pt-1 border-t border-border/60 space-y-2.5 bg-background/30 animate-in fade-in slide-in-from-top-2 duration-150">
          {config.fields.map((field) => {
            const contrastAgainstColor = field.contrastWith ? tokens[field.contrastWith] : undefined;
            const contrastAgainstLabel = field.contrastWith
              ? config.fields.find((f) => f.key === field.contrastWith)?.label
              : undefined;

            return (
              <ColorPickerControl
                key={field.key}
                label={field.label}
                description={field.description}
                value={tokens[field.key] || '#000000'}
                defaultValue={defaultTokens[field.key] || '#000000'}
                onChange={(val) => onChangeToken(field.key, val)}
                contrastAgainstColor={contrastAgainstColor}
                contrastAgainstLabel={contrastAgainstLabel}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
